import { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CartItem {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  images: string[];
  vendor_id: string;
  vendor_name?: string;
  quantity: number;
  relay_id?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isSyncing: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();
  const loadedUserIdRef = useRef<string | null>(null);

  // Load cart from Supabase or localStorage
  useEffect(() => {
    const currentUserId = user?.id || null;
    
    // Prevent loading multiple times for the same user
    if (loadedUserIdRef.current === currentUserId && isInitialized) {
      return;
    }
    
    loadedUserIdRef.current = currentUserId;
    
    const loadCart = async () => {
      if (user?.id) {
        const userId = user.id; // Capture userId to avoid dependency issues
        
        // User logged in: load from Supabase
        try {
          setIsSyncing(true);
          const { data: cartData, error } = await supabase
            .from('carts')
            .select(`
              *,
              product:products!fk_carts_product_id (
                id,
                title,
                price_cents,
                currency,
                images,
                vendor_id
              )
            `)
            .eq('user_id', userId);

          if (error) throw error;

          if (cartData && cartData.length > 0) {
            // Deduplicate: if the same product appears multiple times (no UNIQUE constraint),
            // keep the highest quantity to avoid ghost duplicates.
            const deduped = new Map<string, CartItem>();
            for (const item of cartData) {
              const existing = deduped.get(item.product_id);
              if (existing) {
                existing.quantity = Math.max(existing.quantity, item.quantity);
              } else {
                deduped.set(item.product_id, {
                  id: item.product_id,
                  title: item.product?.title,
                  price_cents: item.product?.price_cents,
                  currency: item.product?.currency,
                  images: item.product?.images || [],
                  vendor_id: item.product?.vendor_id,
                  vendor_name: null,
                  quantity: item.quantity,
                  relay_id: item.relay_id
                });
              }
            }
            const cartItems: CartItem[] = Array.from(deduped.values());
            
            setItems(cartItems);
            
            // Sync with localStorage
            localStorage.setItem('nazandoo-cart', JSON.stringify(cartItems));
          } else {
            // No cart in Supabase, check localStorage
            const savedCart = localStorage.getItem('nazandoo-cart');
            if (savedCart) {
              try {
                const localItems = JSON.parse(savedCart);
                setItems(localItems);
                
                // Sync localStorage cart to Supabase
                await syncLocalCartToSupabase(localItems, userId);
              } catch (error) {
                console.error('Error loading cart from localStorage:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error loading cart from Supabase:', error);
          
          // Fallback to localStorage
          const savedCart = localStorage.getItem('nazandoo-cart');
          if (savedCart) {
            try {
              setItems(JSON.parse(savedCart));
            } catch (error) {
              console.error('Error loading cart from localStorage:', error);
            }
          }
        } finally {
          setIsSyncing(false);
        }
      } else {
        // User not logged in: load from localStorage only
        const savedCart = localStorage.getItem('nazandoo-cart');
        if (savedCart) {
          try {
            setItems(JSON.parse(savedCart));
          } catch (error) {
            console.error('Error loading cart from localStorage:', error);
          }
        }
      }
      setIsInitialized(true);
      setIsLoading(false);
    };

    loadCart();
    
    // Listen for storage changes (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'nazandoo-cart' && e.newValue) {
        try {
          const newItems = JSON.parse(e.newValue);
          setItems(newItems);
        } catch (error) {
          console.error('Error syncing cart from storage:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user?.id]);

  // Sync localStorage cart to Supabase when user logs in
  const syncLocalCartToSupabase = async (localItems: CartItem[], userId: string) => {
    if (!userId || localItems.length === 0) return;

    try {
      setIsSyncing(true);
      
      // Insert each item into Supabase
      for (const item of localItems) {
        await supabase
          .from('carts')
          .upsert({
            user_id: userId,
            product_id: item.id,
            vendor_id: item.vendor_id,
            quantity: item.quantity,
            relay_id: item.relay_id
          }, {
            onConflict: 'user_id,product_id'
          });
      }
    } catch (error) {
      console.error('Error syncing cart to Supabase:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Save cart to localStorage and Supabase
  useEffect(() => {
    // Don't sync during initial load or if not initialized
    if (!isInitialized || isLoading) return;
    
    // Always save to localStorage
    localStorage.setItem('nazandoo-cart', JSON.stringify(items));

    // If user is logged in, sync to Supabase
    if (user?.id) {
      const userId = user.id; // Capture userId to avoid dependency on user object
      
      const syncToSupabase = async () => {
        try {
          setIsSyncing(true);
          
          // Delete all existing cart items for this user
          await supabase
            .from('carts')
            .delete()
            .eq('user_id', userId);

          // Insert current cart items
          if (items.length > 0) {
            const cartData = items.map(item => ({
              user_id: userId,
              product_id: item.id,
              vendor_id: item.vendor_id,
              quantity: item.quantity,
              relay_id: item.relay_id
            }));

            await supabase
              .from('carts')
              .insert(cartData);
          }
        } catch (error) {
          console.error('Error syncing cart to Supabase:', error);
        } finally {
          setIsSyncing(false);
        }
      };

      // Debounce sync to avoid too many requests
      const timeoutId = setTimeout(syncToSupabase, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [items, isInitialized, isLoading, user?.id]);

  const addItem = useCallback((newItem: Omit<CartItem, 'quantity'>, quantity = 1) => {
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === newItem.id);
      
      if (existingItem) {
        toast({
          title: "Quantité mise à jour",
          description: `${newItem.title} - Quantité: ${existingItem.quantity + quantity}`
        });
        return currentItems.map(item =>
          item.id === newItem.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        toast({
          title: "Produit ajouté au panier",
          description: `${newItem.title} a été ajouté à votre panier`
        });
        return [...currentItems, { ...newItem, quantity }];
      }
    });
  }, [toast]);

  const removeItem = useCallback((itemId: string) => {
    setItems(currentItems => {
      const item = currentItems.find(item => item.id === itemId);
      if (item) {
        toast({
          title: "Produit retiré",
          description: `${item.title} a été retiré de votre panier`
        });
      }
      return currentItems.filter(item => item.id !== itemId);
    });
  }, [toast]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item =>
        item.id === itemId
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    toast({
      title: "Panier vidé",
      description: "Tous les produits ont été retirés de votre panier"
    });
  }, [toast]);

  const value = useMemo(() => {
    console.log('[useCartSync] useMemo recalculating context value, items count:', items.length);
    // Calculer les totaux DANS le useMemo pour éviter de recréer les fonctions
    const getTotalItems = () => items.reduce((total, item) => total + item.quantity, 0);
    const getTotalPrice = () => items.reduce((total, item) => total + (item.price_cents * item.quantity), 0);
    
    return {
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getTotalItems,
      getTotalPrice,
      isOpen,
      setIsOpen,
      isSyncing
    };
  }, [items, addItem, removeItem, updateQuantity, clearCart, isOpen, isSyncing]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
